import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ResetPasswordPreview from './pages/auth/ResetPasswordPreview';
import TermsAndConditions from './pages/terms/TermsAndConditions'
import PrivacyPolicy from './pages/privacy/PrivacyPolicy'
import AboutUs from './pages/about/AboutUs';
import ContactUs from './pages/contact/ContactUs';
import Home from './pages/home/Home';
import Dashboard from './pages/dashboard/Dashboard';
import Profile from './pages/profile/Profile';
import TimeSheets from './pages/timesheets/TimeSheets';
import TimesheetEditPage from './pages/timesheets/TimesheetEditPage';
import ExpenseSheets from './pages/expenses/ExpenseSheets';
import ExpenseEditPage from './pages/expenses/ExpenseEditPage';
import { ExpenseProvider } from './pages/expenses/context/ExpenseContext';
import TimesheetApprovals from './pages/approvals/TimesheetApprovals';
import TimesheetViewPage from './pages/approvals/TimesheetViewPage';
import ExpenseApprovals from './pages/approvals/ExpenseApprovals';
import ExpenseViewPage from './pages/approvals/ExpenseViewPage';
import Settings from './pages/settings/Settings';
import Help from './pages/help/Help';
import Documents from './pages/documents/Documents';
import AddDocument from './pages/documents/AddDocument'; 

import { TimesheetDateProvider, ExpenseSheetDateProvider } from './pages/approvals/context/DateContext.js';
import { DocumentsProvider } from './pages/documents/context/DocumentsContext.js';
import { TimesheetProvider } from './pages/timesheets/context/TimesheetContext.js';

import { SessionProvider, useAuthToken } from './auth';

function App() {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { logout } = useAuthToken();

  return (
    <SessionProvider>
      <div className="app-container">
            <Routes>
              {/* Public routes */}
              <Route path="/home" element={<Home />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />

              {/* Auth routes */}
              <Route
                path="/login"
                element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />}
              />
              <Route
                path="/forgot-password"
                element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" replace />}
              />
              <Route
                path="/reset-password"
                element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/dashboard" replace />}
              />
              <Route path="/reset-password-preview" element={<ResetPasswordPreview />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Layout onLogout={() => logout()} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<Profile />} />

                {/* Timesheets */}
                <Route
                  path="timesheets/*"
                  element={
                    <TimesheetProvider>
                      <Routes>
                        <Route index element={<TimeSheets />} />
                        <Route path="new" element={<TimesheetEditPage mode="add" />} />
                        <Route path="edit/:id" element={<TimesheetEditPage mode="edit" />} />
                      </Routes>
                    </TimesheetProvider>
                  }
                />

                {/* Expenses */}
                <Route
                  path="expenses/*"
                  element={
                    <ExpenseProvider>
                      <Routes>
                        <Route index element={<ExpenseSheets />} />
                        <Route path="new" element={<ExpenseEditPage mode="add" />} />
                        <Route path="edit/:id" element={<ExpenseEditPage mode="edit" />} />
                      </Routes>
                    </ExpenseProvider>
                  }
                />

                {/*Approvals */}
                <Route
                  path="approvals/*"
                  element={
                    <TimesheetDateProvider>
                      <ExpenseSheetDateProvider>
                        <Routes>
                          <Route index element={<Navigate to="timesheetapprovals" replace />} />
                          <Route path="timesheetapprovals" element={<TimesheetApprovals />} />
                          <Route path="timesheetapprovals/view/:id" element={<TimesheetViewPage />} />
                          <Route path="expenseapprovals" element={<ExpenseApprovals />} />
                          <Route path="expenseapprovals/view/:id" element={<ExpenseViewPage />} />
                        </Routes>
                      </ExpenseSheetDateProvider>
                    </TimesheetDateProvider>
                  }
                />

                {/* Documents */}
                <Route
                  path="documents/*"
                  element={
                    <DocumentsProvider>
                      <Routes>
                        <Route index element={<Documents />} />
                        <Route path="add" element={<AddDocument />} />
                      </Routes>
                    </DocumentsProvider>
                  }
                />

                {/* Other pages */}
                <Route path="settings" element={<Settings />} />
                <Route path="help" element={<Help />} />
              </Route>
            </Routes>
      </div>
    </SessionProvider>
  );
}

export default App;